import type { ComponentType, SVGProps } from 'react';
import type { Entity } from '@backstage/catalog-model';
import type { IconComponent } from '@backstage/core-plugin-api';
import {
  ResourceAmazonAuroraInstance,
  ResourceAmazonSimpleStorageServiceS3Standard,
  ResourceAmazonSimpleQueueServiceMessage,
  ResourceAmazonSimpleNotificationServiceTopic,
  ResourceAmazonMSKAmazonMSKConnect,
  ResourceAmazonDynamoDBTable,
  ArchitectureGroupAWSCloudlogoDark,
  ArchitectureGroupAWSCloudDark,
} from 'aws-react-icons';

type AwsIcon = ComponentType<SVGProps<SVGElement> & { size?: number | string }>;

// aws-react-icons only size their <svg> via the `size` prop (they ignore
// width/height) and default to 24px. Backstage renders the presentation Icon
// in two ways: inline in refs/chips (via fontSize) and inside the relations
// graph, where it passes numeric width/height plus x/y to position the icon in
// an SVG. Forward those props and derive `size` from the passed height so the
// icon scales and positions correctly in both contexts.
const asIcon = (Icon: AwsIcon): IconComponent => {
  const Wrapped = (props: SVGProps<SVGElement> & { fontSize?: unknown }) => {
    const { fontSize: _fontSize, height, style, ...rest } = props;
    const size = height ?? '1em';
    return (
      <Icon
        {...rest}
        height={height}
        size={size}
        style={{ verticalAlign: 'middle', ...(style ?? {}) }}
        aria-hidden
        focusable={false}
      />
    );
  };
  return Wrapped as IconComponent;
};

/**
 * AWS service icons per Resource `spec.type`, so infra resources and AWS
 * accounts render with a recognisable icon instead of the generic resource
 * glyph. Types map 1:1 with the infra categories used by the catalog
 * generator (aurora-postgres, s3, sqs, sns, msk, aws-account) plus environment.
 */
const RESOURCE_TYPE_ICONS: Record<string, IconComponent> = {
  'aurora-postgres': asIcon(ResourceAmazonAuroraInstance),
  dynamodb: asIcon(ResourceAmazonDynamoDBTable),
  s3: asIcon(ResourceAmazonSimpleStorageServiceS3Standard),
  sqs: asIcon(ResourceAmazonSimpleQueueServiceMessage),
  sns: asIcon(ResourceAmazonSimpleNotificationServiceTopic),
  msk: asIcon(ResourceAmazonMSKAmazonMSKConnect),
  'aws-account': asIcon(ArchitectureGroupAWSCloudlogoDark),
  environment: asIcon(ArchitectureGroupAWSCloudDark),
};

/**
 * Returns the AWS icon for a Resource entity based on its `spec.type`, or
 * undefined for non-Resource entities / unmapped types (keeps the default).
 */
export function getResourceIcon(
  entity: Entity | undefined,
): IconComponent | undefined {
  if (!entity || entity.kind.toLocaleLowerCase('en-US') !== 'resource') {
    return undefined;
  }
  const type = (entity.spec as Record<string, unknown> | undefined)?.type;
  return typeof type === 'string' ? RESOURCE_TYPE_ICONS[type] : undefined;
}
